import React, {Component} from "react";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import HistoryPopoverContent from "./HistoryPopoverContent";
import HistoryPopoverTitle from "./HistoryPopoverTitle";
import "./HistoryPopover.css";

export default class HistoryPopover extends Component {

    triggerRef = React.createRef();
    resizeObserver = null;

    state = {
        rowWidth: null,
        xOffset: 0
    };

    componentDidMount() {
        this.computeLayout();
    }

    componentDidUpdate(prevProps) {
        if (this.props.fullWidth && !prevProps.fullWidth) {
            this.computeLayout();
        }
        if (!this.props.fullWidth && prevProps.fullWidth && this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    // Compute desired width (row width) and horizontal shift to align popover with Item Number cell
    computeLayout = () => {
        const triggerEl = this.triggerRef.current;
        if (!triggerEl) return;

        const iconCell = triggerEl.closest("td");
        const row = iconCell ? iconCell.parentElement : null;
        if (!row) return;

        // Assume Item Number column is the second column (index 1). Adjust if different.
        // If partNumber has a link, its text still lives inside cell children.
        const partNumberCell = Array.from(row.children)[1] || iconCell;

        const rowRect = row.getBoundingClientRect();
        const iconRect = iconCell.getBoundingClientRect();
        const partRect = partNumberCell.getBoundingClientRect();

        const viewportMax = window.innerWidth - 32; // 16px margin each side
        const desiredWidth = Math.min(rowRect.width, viewportMax);

        // Shift left so popover left aligns with Item Number cell left
        const xOffset = partRect.left - iconRect.left;

        this.setState({
            rowWidth: desiredWidth,
            xOffset
        }, () => {
            // Observe row width changes (window resize, responsive table)
            if (!this.resizeObserver) {
                this.resizeObserver = new ResizeObserver(entries => {
                    for (const entry of entries) {
                        const newWidth = Math.min(entry.contentRect.width, window.innerWidth - 32);
                        if (newWidth !== this.state.rowWidth) {
                            this.setState({ rowWidth: newWidth });
                        }
                    }
                });
                this.resizeObserver.observe(row);
            }
        });
    };

    render() {
        const {
            inventorySkuPK,
            historyType,
            pageText,
            partNumber,
            inventoryPK,
            triggerContent,
            handleHistoryError,
            maxHeight = 400
        } = this.props;

        // Fallback width if measurement not yet done
        const finalWidth = this.state.rowWidth || 800;

        const popover = (
            <Popover
                id={`historyPopover-${inventorySkuPK}-${historyType}`}
                className="history-popover history-popover-fullrow"
                style={{
                    width: finalWidth,
                    maxWidth: finalWidth,
                    maxHeight,
                    display: "flex",
                    flexDirection: "column"
                }}
            >
                <Popover.Header as="div" className="history-popover-header">
                    <HistoryPopoverTitle
                        historyType={historyType}
                        pageText={pageText}
                        partNumber={partNumber}
                    />
                </Popover.Header>
                <Popover.Body className="history-popover-body">
                    <div className="history-fullrow-table-wrapper">
                        <HistoryPopoverContent
                            inventoryPK={inventoryPK}
                            inventorySkuPK={inventorySkuPK}
                            historyType={historyType}
                            pageText={pageText}
                            handleHistoryError={handleHistoryError}
                            compact={false}
                            scrollX={false}
                        // We want the full table visible; no horizontal scroll in screenshot
                        />
                    </div>
                </Popover.Body>
            </Popover>
        );

        return (
            <OverlayTrigger
                delay={{ show: 150, hide: 300 }}
                overlay={popover}
                placement="top-start"
                trigger={["hover", "focus"]}
                popperConfig={{
                    modifiers: [
                        {
                            name: "offset",
                            options: { offset: [this.state.xOffset, 8] }
                        },
                        {
                            name: "preventOverflow",
                            options: { boundary: "viewport", padding: 8, altAxis: true }
                        },
                        {
                            name: "flip",
                            options: { fallbackPlacements: ["bottom-start"] }
                        }
                    ]
                }}
            >
                <span
                    ref={this.triggerRef}
                    className="history-trigger-wrapper"
                    tabIndex={0}
                    onMouseEnter={this.computeLayout}
                >
                    {triggerContent}
                </span>
            </OverlayTrigger>
        );
    }
}